import { campaignsApi, CreateCampaignParams } from "@/api/campaign.api";
import { useUsersQuery } from "@/api/users.api";
import { Campaign } from "@/types/campaign";
import { queryClient } from "@/utils/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { omit } from "lodash-es";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { Button } from "../generic/Button";
import { Heading } from "../generic/Heading";
import { Modal } from "../generic/Modal";
import { Select, SelectItem } from "../generic/Select";
import { TextField } from "../generic/TextField";

export type Props = {
  campaign: Campaign;
  isOpen: boolean;
  onOpenChange: (state: boolean) => void;
  onCopyCampaign?: (copiedCampaign: Campaign) => void;
};

export function CopyCampaignModal({
  campaign,
  isOpen,
  onOpenChange,
  onCopyCampaign,
}: Props) {
  const { data: usersData } = useUsersQuery({ status: "active" });

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["createCampaign"],
    mutationFn: async (params: CreateCampaignParams) => {
      const createdCampaign = await campaignsApi.create(params, {
        startNow: true,
        sendNow: false,
      });

      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["simpleCampaigns"] });
      queryClient.invalidateQueries({ queryKey: ["credits"] });

      return createdCampaign;
    },
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    return usersData.pages.flatMap((page) => page.results);
  }, [usersData]);

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(
      z.object({
        copyOfCampaignName: z.string().min(1, "Navn er påkrævet"),
        copyResponsibleId: z.string(),
      }),
    ),
    defaultValues: {
      copyOfCampaignName: `${campaign.name} (kopi)`,
      copyResponsibleId: campaign.responsibleId?.toString() ?? "",
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const createCampaignParams = {
      name: data.copyOfCampaignName,
      responsibleId: data.copyResponsibleId,

      configurations: campaign.configurations.map((c) =>
        omit(c, "id", "campaignId"),
      ),
      stopLists: campaign.stopLists,

      type: campaign.type,
      schedules: campaign.schedules.map((s) =>
        omit(s, "id", "scheduleChanges"),
      ),
      triggers: campaign.triggers.map((t) => omit(t, "id")),
      maxLeadsPerWeek: campaign.maxLeadsPerWeek,
      chosenStartDate: null,
      chosenEndDate: null,

      deliverContacts: campaign.deliverContacts,
      deliverContactsRequirePhone: campaign.deliverContactsRequirePhone,
      deliverLiveNumber: campaign.deliverLiveNumber,

      exportMode: campaign.exportMode,
      exportSystem: campaign.exportSystem,
      exportInput: campaign.exportInput,
      completionEmail: campaign.completionEmail,
    } satisfies Required<CreateCampaignParams>;

    const copiedCampaign = await mutateAsync(createCampaignParams);
    onOpenChange(false); // Close the modal after submission
    onCopyCampaign?.(copiedCampaign); // Notify parent component
  });

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      aria-label="Dupliker kampagne"
    >
      <div className="mb-8 flex flex-col items-center justify-center gap-y-3">
        <Heading level={1}>Dupliker kampagne</Heading>
        <p className="text-theme-on-background-variant text-center text-xs">
          Lav en ny kampagne med samme opsætning. Kampagnen vil starte fra dags
          dato.
        </p>
      </div>
      <form
        className="flex w-full flex-col justify-center gap-6"
        onSubmit={onSubmit}
      >
        <TextField
          name="copyOfCampaignName"
          control={control}
          label="Kampagnenavn"
        />
        <Controller
          name="copyResponsibleId"
          control={control}
          render={({ field }) => (
            <Select
              label="Ansvarlig"
              selectedKey={field.value}
              onSelectionChange={field.onChange}
              items={users.map((u) => ({
                label: `${u.name} (${u.email})`,
                value: u.id.toString(),
              }))}
            >
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </Select>
          )}
        />
        <div className="flex justify-center gap-4">
          <Button
            className="mt-4"
            intent="secondary"
            onPress={() => onOpenChange(false)}
          >
            Fortryd
          </Button>
          <Button className="mt-4" type="submit" isDisabled={isPending}>
            {isPending ? "Opretter kampagne..." : "Dupliker kampagne"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
